import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Check, Hourglass } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { company: '', contactPerson: '', email: '', phone: '', branche: '', notes: '' };
const detail = (err, fallback) => {
  const d = err?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};

const FIELDS = [
  ['company', 'Unternehmen *', 'text'],
  ['contactPerson', 'Ansprechpartner *', 'text'],
  ['email', 'E-Mail *', 'email'],
  ['phone', 'Telefon', 'tel'],
  ['branche', 'Branche', 'text'],
];

export default function WaitlistDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.company.trim() && form.contactPerson.trim() && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim());

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/waitlist`, form);
      setResult(data);
      setForm(EMPTY);
      toast.success('Sie stehen auf der Warteliste.');
    } catch (err) { toast.error(detail(err, 'Eintrag fehlgeschlagen.')); }
    finally { setBusy(false); }
  };

  const close = (v) => { onOpenChange(v); if (!v) setResult(null); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-[#282C37] bg-[#16181D] text-white sm:max-w-lg" data-testid="waitlist-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">Warteliste Gründungspartner</DialogTitle>
          <DialogDescription className="text-[#B6BCC8]">
            Alle zehn Plätze sind aktuell belegt. Wir melden uns, sobald ein Platz frei wird – ohne Verpflichtung.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="py-4" data-testid="waitlist-success">
            <p className="flex items-center gap-2 font-mono-num text-xs font-bold uppercase tracking-[0.2em] text-[#FF6B00]"><Check className="h-4 w-4" /> Eingetragen</p>
            <p className="mt-4 flex items-center gap-3 font-display text-3xl font-extrabold tracking-tight">
              <Hourglass className="h-7 w-7 text-[#FF6B00]" /> Platz {result.position}
            </p>
            <p className="t-body mt-4">{result.message}</p>
            <button type="button" onClick={() => close(false)} data-testid="waitlist-close" className="btn-ghost mt-6 w-full !py-3">Schließen</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 py-2">
            <div className="grid gap-4 sm:grid-cols-2">
              {FIELDS.map(([key, label, type]) => (
                <div key={key} className={key === 'branche' ? 'sm:col-span-2' : ''}>
                  <label className="label" htmlFor={`wl-${key}`}>{label}</label>
                  <input id={`wl-${key}`} type={type} value={form[key]} onChange={set(key)} className="input" data-testid={`waitlist-${key}`} />
                </div>
              ))}
            </div>
            <div>
              <label className="label" htmlFor="wl-notes">Nachricht</label>
              <textarea id="wl-notes" rows={3} value={form.notes} onChange={set('notes')} className="input !h-auto resize-y" data-testid="waitlist-notes" />
            </div>
            <button type="submit" disabled={busy || !valid} data-testid="waitlist-submit" className="btn-primary w-full !py-3 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Auf die Warteliste
            </button>
            <p className="t-muted !text-xs">Wir nutzen Ihre Daten ausschließlich, um Sie zu Gründungspartner-Plätzen zu kontaktieren.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
